<script setup>
import { computed, ref, useId } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PROVIDERS } from '../helpers';
import { useAdsI18n } from '../useAdsI18n';

const props = defineProps({
  summary: {
    type: Object,
    required: true,
  },
});

const { t } = useAdsI18n();

const expanded = ref(false);
const trackingDetailsId = `ads-conversation-tracking-${useId()}`;
const isWhatsappReferral = computed(
  () => props.summary.source === 'whatsappReferral'
);
const providerLabel = computed(() =>
  t(
    `conversation.${isWhatsappReferral.value ? 'metaWhatsapp' : props.summary.provider}`
  )
);
const providerIcon = computed(
  () => PROVIDERS[props.summary.provider]?.icon || 'i-lucide-badge-dollar-sign'
);
const evidenceState = computed(() => {
  if (props.summary.evidence === 'complete') {
    return {
      icon: 'i-lucide-circle-check',
      color: 'text-n-teal-11',
    };
  }

  if (props.summary.evidence === 'partial') {
    return {
      icon: 'i-lucide-circle-dashed',
      color: 'text-n-amber-11',
    };
  }

  return {
    icon: 'i-lucide-circle-help',
    color: 'text-n-slate-11',
  };
});
const evidenceLabel = computed(() => {
  if (props.summary.evidence === 'complete') {
    return t('conversation.complete');
  }
  if (props.summary.evidence === 'partial') {
    return t('conversation.partial');
  }
  return t('conversation.unknownEvidence');
});
const toggleLabel = computed(() =>
  expanded.value ? t('conversation.hideTrace') : t('conversation.viewTrace')
);
</script>

<template>
  <section
    class="mb-3 rounded-xl bg-n-solid-1 px-3 py-2.5 outline outline-1 outline-n-container"
    data-ads-conversation-tracking
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="flex size-7 flex-shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 outline outline-1 outline-n-weak"
        >
          <span aria-hidden="true" class="size-4" :class="providerIcon" />
        </span>
        <div class="min-w-0">
          <p class="m-0 text-label-small leading-3 text-n-slate-10">
            {{ t('conversation.compactTitle') }}
          </p>
          <p class="truncate text-sm font-semibold leading-4 text-n-slate-12">
            {{ providerLabel }}
          </p>
        </div>
      </div>
      <div class="flex flex-shrink-0 items-center gap-1">
        <span
          v-if="summary.evidence !== 'complete'"
          v-tooltip.top="evidenceLabel"
          :aria-label="evidenceLabel"
          role="img"
          tabindex="0"
          :data-evidence-state="summary.evidence || 'unknown'"
          class="flex size-8 items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-n-blue-8"
          :class="evidenceState.color"
        >
          <span class="size-4" :class="evidenceState.icon" />
        </span>
        <Button
          v-tooltip.top="toggleLabel"
          :aria-label="toggleLabel"
          :aria-expanded="expanded"
          :aria-controls="trackingDetailsId"
          :icon="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          color="slate"
          variant="ghost"
          size="sm"
          @click="expanded = !expanded"
        />
      </div>
    </div>

    <div
      v-if="expanded"
      :id="trackingDetailsId"
      class="mt-3 grid gap-2 border-t border-n-weak pt-3"
    >
      <div class="flex min-w-0 items-start justify-between gap-2">
        <div v-if="summary.campaign" class="min-w-0">
          <p
            v-if="!isWhatsappReferral"
            class="m-0 text-label-small leading-3 text-n-slate-9"
          >
            {{ t('conversation.campaign') }}
          </p>
          <p
            class="truncate text-label-small font-semibold leading-4 text-n-slate-12"
          >
            {{ summary.campaign }}
          </p>
        </div>
        <span
          v-tooltip.top="t('conversation.capturedBeforeConversation')"
          :aria-label="t('conversation.capturedBeforeConversation')"
          role="img"
          tabindex="0"
          class="ml-auto flex size-7 flex-shrink-0 items-center justify-center text-n-slate-10 outline-none focus-visible:ring-2 focus-visible:ring-n-blue-8"
        >
          <span class="i-lucide-clock-3 size-3.5" />
        </span>
      </div>

      <div v-if="summary.identifier" class="flex justify-between gap-3">
        <span class="text-label-small leading-4 text-n-slate-9">
          {{ t('conversation.identifier') }}
        </span>
        <span
          class="truncate text-label-small font-semibold leading-4 text-n-slate-12"
        >
          {{ summary.identifierType }} · {{ summary.identifier }}
        </span>
      </div>
      <div v-if="summary.entryPage" class="flex justify-between gap-3">
        <span class="text-label-small leading-4 text-n-slate-9">
          {{ t('conversation.entryPage') }}
        </span>
        <span class="flex min-w-0 items-center gap-1">
          <span
            class="truncate text-label-small font-semibold leading-4 text-n-slate-12"
          >
            {{ summary.entryPage }}
          </span>
          <a
            v-if="summary.pageUrl"
            v-tooltip.top="t('conversation.openPage')"
            :href="summary.pageUrl"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="t('conversation.openPage')"
            class="flex size-7 shrink-0 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-8"
          >
            <span aria-hidden="true" class="i-lucide-external-link size-3.5" />
          </a>
        </span>
      </div>
      <div class="flex justify-between gap-3">
        <span class="text-label-small leading-4 text-n-slate-9">
          {{ t('conversation.source') }}
        </span>
        <span class="text-label-small font-semibold leading-4 text-n-slate-12">
          {{ t(`conversation.${summary.source}`) }}
        </span>
      </div>
    </div>
  </section>
</template>
